import { Navigate, useRoutes } from "react-router-dom";

// layouts
import DashboardLayout from "../layouts/dashboard";
import LogoOnlyLayout from "../layouts/LogoOnlyLayout";

// guards
import GuestGuard from "../guards/GuestGuard";
import AuthGuard from "../guards/AuthGuard";

// Auth
import Login from "@pages/auth/Login";
import MagicLink from "@pages/auth/MagicLink";

// ETF
import ETFPage from "@pages/asset/etf/all";

// Stock
import StockPage from "@pages/asset/stock/all";

// Fund / Index / Realty — develop 에서 옮겨온 화면
import Fundkr from "@pages/asset/fund";
import IndexGlobal from "@pages/asset/indexAsset/global";
import RealtyIndexPage from "@pages/asset/realty/realty_index";
import { PERMISSIONS, RequirePermission } from "@auth";
import { PATH_ADMIN } from "@routes/paths";

// Backtest
import StaticBacktest from "@pages/backtest/StaticBacktest";

// User
import User from "@pages/user";

// Admin
import Admin from "@pages/admin";
import AdminUserPanel from "@pages/admin/UserPanel";
import AdminMembershipPanel from "@pages/admin/MembershipPanel";
import AdminIdentityPanel from "@pages/admin/IdentityPanel";

// ETC
import NotFound from "@pages/Page404";
import Page500 from "@components/error/Page500";
import SuspenseContainer from "@components/SuspenseContainer";

function Router() {
  const routes = useRoutes([
    {
      path: "auth",
      children: [
        {
          path: "login",
          element: (
            <GuestGuard>
              <Login/>
            </GuestGuard>
          ),
        },
        { path: "login-unprotected", element: <Login/> },
        // `GuestGuard` 를 씌우지 않는다. 이미 로그인한 사람이 메일의 링크를 눌렀을 때
        // 대시보드로 튕겨 버리면 «이 링크가 왜 안 되지» 가 된다. 재설정과 같은 이유다.
        { path: "magic", element: <MagicLink/> },
      ],
    },
    {
      path: "/",
      element: (
        <AuthGuard>
          <DashboardLayout/>
        </AuthGuard>
      ),
      children: [
        {
          path: "/",
          element: <Navigate replace to="/etf"/>
        },
        {
          path: "etf",
          element: (
            <SuspenseContainer>
              <ETFPage/>
            </SuspenseContainer>
          ),
        },
        {
          path: "stock",
          element: (
            <SuspenseContainer>
              <StockPage/>
            </SuspenseContainer>
          ),
        },
        {
          path: "fund/kr",
          element: (
            <SuspenseContainer>
              <Fundkr/>
            </SuspenseContainer>
          ),
        },
        {
          path: "asset/index/global",
          element: (
            <SuspenseContainer>
              <IndexGlobal/>
            </SuspenseContainer>
          ),
        },
        {
          // 부동산은 elite 전용이다 (정재영님 요청 B). 메뉴에서 감추는 것만으로는
          // URL 을 직접 치면 열리므로 라우트에서도 막는다. 데이터는 서버가 403 이다.
          path: "realty/realty_indexes",
          element: (
            <RequirePermission permission={PERMISSIONS.REALTY_VIEW}>
              <SuspenseContainer>
                <RealtyIndexPage/>
              </SuspenseContainer>
            </RequirePermission>
          ),
        },
        {
          path: "backtest/static",
          element: <StaticBacktest/>
        },
        {
          path: "user",
          element: (
            <SuspenseContainer>
              <User/>
            </SuspenseContainer>
          ),
        },
        {
          // 메뉴에서 감추는 것만으로는 URL 을 직접 치면 열린다. 라우트에서도 막는다.
          // 데이터는 서버가 `require_level(ADMIN)` 으로 또 막으므로, 이건 화면을
          // 안 보여주기 위한 것이지 보안의 마지막 선이 아니다.
          path: "admin",
          element: (
            <RequirePermission permission={PERMISSIONS.ADMIN_PANEL}>
              <SuspenseContainer>
                <Admin/>
              </SuspenseContainer>
            </RequirePermission>
          ),
          children: [
            { path: "", element: <Navigate replace to={PATH_ADMIN.users}/> },
            { path: "users", element: <AdminUserPanel/> },
            { path: "memberships", element: <AdminMembershipPanel/> },
            { path: "identity", element: <AdminIdentityPanel/> },
          ],
        },
      ],
    },
    {
      path: "error",
      element: <LogoOnlyLayout/>,
      children: [{ path: "500", element: <Page500/> }],
    },
    {
      path: "*",
      element: <LogoOnlyLayout/>,
      children: [
        { path: "404", element: <NotFound/> },
        { path: "*", element: <Navigate to="/404" replace/> },
      ],
    },
    {
      path: "*",
      element: <Navigate to="/404" replace/>
    },
  ]);

  return routes;
}

export default Router;
